import { legacyLocalLabel, legacyLocalDate } from '../../../utilities/legacyDatePresentation';
import { startOfDay, differenceInDays } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { CalendarDays, FileText, Link2, AlertCircle, Loader2, MapPin } from 'lucide-react';
import DefaultAvatar from '../Avatar/DefaultAvatar';
import { useParticipantProfileCache } from '../../../contexts/ParticipantProfileCacheContext'; // <-- Import the new hook
import Region from '../../../interfaces/identity/Region';

export interface TooltipParticipant {
    id: string | number;
    firstName: string;
    lastName: string;
    email?: string;
    waiver?: string | { waiverName: string; waiverCode: string;[key: string]: any };
    joinDate?: Date | string;
    creationDate?: Date | string;
    linkageDate?: Date | string;
    cpocEndDate?: Date | string;
    cpocDate?: Date | string;
    regionName?: string; 
    region?: Region; 
}

export interface ParticipantProfileTooltipProps {
    participant: TooltipParticipant;
    theme?: 'light' | 'dark';
}

const ParticipantProfileTooltip: React.FC<ParticipantProfileTooltipProps> = ({
    participant,
    theme = 'light'
}) => {
    // 1. Consume the new cache context
    const { getParticipantProfile } = useParticipantProfileCache();

    const [fullData, setFullData] = useState<TooltipParticipant | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const isDark = theme === 'dark';
    const borderClass = isDark ? 'border-[#3a3a3c]' : 'border-gray-100';
    const primaryText = isDark ? 'text-[#f9f9fb]' : 'text-gray-900';
    const secondaryText = isDark ? 'text-[#ebebf5]/60' : 'text-gray-500';
    const iconColor = isDark ? 'text-[#ebebf5]/60' : 'text-gray-400';
    const valueText = isDark ? 'text-[#ebebf5]/80' : 'text-gray-700';

    useEffect(() => {
        // If we miraculously have all deep data already, skip fetch
        if (participant.linkageDate && (participant.cpocDate || participant.cpocEndDate) && (participant.region?.regionName || typeof participant.region === 'object')) {
            setFullData(participant);
            return;
        }

        let isMounted = true;
        const loadData = async () => {
            setIsLoading(true);
            try {
                const data = await getParticipantProfile(participant.id);
                if (isMounted && data) setFullData(data);
            } catch (e) {
                console.error("Failed to fetch participant tooltip data", e);
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };

        loadData();

        return () => { isMounted = false; };
    }, [participant, getParticipantProfile]);

    const displayData = fullData || participant;

    // --- Safe Extractors ---
    const actualJoinDate = displayData.joinDate || displayData.creationDate;
    const actualCpocDate = displayData.cpocEndDate || displayData.cpocDate;

    const waiverDisplay = typeof displayData.waiver === 'string'
        ? displayData.waiver
        : displayData.waiver?.waiverName || displayData.waiver?.waiverCode;

    // Handle Region Name (whether it comes as a string, nested object, or an integer ID)
    let regionDisplay = displayData.regionName;
    if (!regionDisplay) {
        if (typeof displayData.region === 'object' && displayData.region !== null) {
            regionDisplay = (displayData.region as any).regionName;
        } else if (typeof displayData.region === 'number' || typeof displayData.region === 'string') {
            // If the API just passes back the ID number (e.g., 100), format it nicely
            regionDisplay = `Region ${displayData.region}`;
        }
    }

    // --- CPOC Color Logic ---
    let cpocColorClass = valueText;
    let cpocIconColor = iconColor;

    if (actualCpocDate && Number.isFinite(+legacyLocalDate(actualCpocDate))) {
        const today = startOfDay(new Date());
        const cpoc = startOfDay(legacyLocalDate(actualCpocDate));
        const diffDays = differenceInDays(cpoc, today);

        if (diffDays < 0) {
            cpocColorClass = 'text-red-500 font-semibold';
            cpocIconColor = 'text-red-500';
        } else if (diffDays <= 7) {
            cpocColorClass = 'text-amber-500 font-semibold';
            cpocIconColor = 'text-amber-500';
        }
    }

    // Defensive formatting to prevent "Invalid Date" UI errors
    const formatDate = (dateString?: Date | string) => {
        if (!dateString) return '--';
        const m = legacyLocalDate(dateString);
        return Number.isFinite(+m) ? legacyLocalLabel(m, "MM/dd/yyyy") : '--';
    };

    return (
        <div className="flex flex-col gap-3 w-64 p-1 transition-all duration-300">
            {/* Header */}
            <div className={`flex items-center gap-3 border-b pb-3 ${borderClass}`}>
                <DefaultAvatar
                    userId={String(participant.id)}
                    displayName={`${participant.firstName} ${participant.lastName}`}
                    size="sm"
                />
                <div className="overflow-hidden">
                    <h4 className={`font-semibold text-sm m-0 truncate ${primaryText}`}>
                        {participant.firstName} {participant.lastName}
                    </h4>
                    <p className={`text-xs m-0 truncate ${secondaryText}`}>
                        {displayData.email || 'No email provided'}
                    </p>
                </div>
            </div>

            {/* Details Area */}
            <div className="flex flex-col gap-2 text-xs font-poppins min-h-[60px] relative justify-center">
                {isLoading && !fullData ? (
                    <div className="absolute inset-0 flex items-center justify-center">
                        <Loader2 size={18} className={`animate-spin ${iconColor}`} />
                    </div>
                ) : (
                    <div className="animate-in fade-in duration-300 flex flex-col gap-2">

                        {/* Region */}
                        {regionDisplay && (
                            <div className={`flex items-start justify-between ${valueText}`}>
                                <span className={`flex items-center gap-1.5 ${iconColor} whitespace-nowrap mt-0.5`}>
                                    <MapPin size={14} /> Region
                                </span>
                                <span className="text-right line-clamp-2 max-w-[150px] leading-tight" title={regionDisplay}>
                                    {regionDisplay}
                                </span>
                            </div>
                        )}

                        {/* Waiver */}
                        {waiverDisplay && (
                            <div className={`flex items-start justify-between ${valueText}`}>
                                <span className={`flex items-center gap-1.5 ${iconColor} whitespace-nowrap mt-0.5`}>
                                    <FileText size={14} /> Waiver
                                </span>
                                <span className="text-right line-clamp-2 max-w-[150px] leading-tight" title={waiverDisplay}>
                                    {waiverDisplay}
                                </span>
                            </div>
                        )}

                        {/* Join Date */}
                        {actualJoinDate && Number.isFinite(+legacyLocalDate(actualJoinDate)) && (
                            <div className={`flex items-center justify-between ${valueText}`}>
                                <span className={`flex items-center gap-1.5 ${iconColor}`}>
                                    <CalendarDays size={14} /> Joined
                                </span>
                                <span>{formatDate(actualJoinDate)}</span>
                            </div>
                        )}

                        {/* Linkage Date */}
                        {displayData.linkageDate && Number.isFinite(+legacyLocalDate(displayData.linkageDate)) && (
                            <div className={`flex items-center justify-between ${valueText}`}>
                                <span className={`flex items-center gap-1.5 ${iconColor}`}>
                                    <Link2 size={14} /> Linkage
                                </span>
                                <span>{formatDate(displayData.linkageDate)}</span>
                            </div>
                        )}

                        {/* CPOC End Date */}
                        {actualCpocDate && Number.isFinite(+legacyLocalDate(actualCpocDate)) && (
                            <div className={`flex items-center align-middle justify-between ${cpocColorClass}`}>
                                <span className={`flex items-center align-middle justify-center gap-1.5 ${cpocIconColor}`}>
                                    <AlertCircle size={14} /> CPOC End
                                </span>
                                <span>{formatDate(actualCpocDate)}</span>
                            </div>
                        )}

                        {/* Fallback */}
                        {!actualJoinDate && !displayData.linkageDate && !actualCpocDate && !waiverDisplay && !regionDisplay && (
                            <div className={`text-center py-2 italic ${secondaryText}`}>
                                No additional details available
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default ParticipantProfileTooltip;